'use client'
import { getClientSideURL } from '@/utilities/getURL'
import { RefreshRouteOnSave as PayloadLivePreview } from '@payloadcms/live-preview-react'
import { useRouter } from 'next/navigation'
import React from 'react'

/**
 * LivePreviewListener - Real-time content preview system for Payload CMS
 * 
 * @description Enables live preview functionality that automatically refreshes page content
 * when changes are made in the Payload CMS admin panel. This component establishes a 
 * WebSocket connection with the Payload server to listen for content updates and triggers
 * client-side route refreshes without full page reloads, providing a seamless editing
 * experience for content creators.
 * 
 * @example
 * // Basic usage in root layout for global live preview
 * export default function RootLayout({ children }: { children: React.ReactNode }) {
 *   return (
 *     <html>
 *       <body>
 *         {children}
 *         <LivePreviewListener />
 *       </body>
 *     </html>
 *   )
 * }
 * 
 * @example
 * // Usage in page components for specific preview contexts
 * export default function BlogPost({ post }: { post: Post }) {
 *   return (
 *     <div>
 *       <h1>{post.title}</h1>
 *       <div>{post.content}</div>
 *       <LivePreviewListener />
 *     </div>
 *   )
 * }
 * 
 * @example
 * // Integration with preview mode detection
 * import { draftMode } from 'next/headers'
 * 
 * export default function Page() {
 *   const { isEnabled } = draftMode()
 *   
 *   return (
 *     <div>
 *       <main>Page content here</main>
 *       {isEnabled && <LivePreviewListener />}
 *     </div>
 *   )
 * }
 * 
 * @example
 * // Using with custom refresh handlers
 * function CustomPreviewWrapper() {
 *   const router = useRouter()
 *   const handleRefresh = useCallback(() => {
 *     // Custom logic before refresh
 *     console.log('Content updated, refreshing...')
 *     router.refresh()
 *   }, [router])
 *   
 *   return (
 *     <PayloadLivePreview 
 *       refresh={handleRefresh} 
 *       serverURL={getClientSideURL()} 
 *     />
 *   )
 * }
 * 
 * @example
 * // Integration in layout builder blocks
 * export function RenderBlocks({ blocks }: { blocks: Block[] }) {
 *   return (
 *     <>
 *       {blocks.map((block, index) => (
 *         <BlockRenderer key={index} block={block} />
 *       ))}
 *       <LivePreviewListener />
 *     </>
 *   )
 * }
 */
export const LivePreviewListener: React.FC = () => {
  const router = useRouter()
  return <PayloadLivePreview refresh={router.refresh} serverURL={getClientSideURL()} />
}
